/* global G */
import find from 'lib/sequence/component/children/find'
import { getFirstItem } from 'lib/util'
import { get } from 'lib/sequence/component/state/value'

/**
 * @typedef {Object} ComponentInfo
 * @property {string} parent    parent of the component in the component tree
 * @property {string} component component to use
 *
 * @typedef {Object} AttributeInfo
 * @property {string} parent    parent of the model in the model tree
 * @property {string} attribute (sub-)model to use
 *
 * @typedef {function(Gaia.AppModule.Spec,Gaia.Component.Spec,Gaia.PlatformEvent): [{
 *     primary: Gaia.Component.Spec,
 *     secondary: Gaia.Component.Spec,
 *     value: *,
 *     c: ComponentInfo,
 *     a?: AttributeInfo
 *   }, Gaia.AppModule.Spec,Gaia.Component.Spec,Gaia.PlatformEvent]
 * } ReturnValue
 */

/**
 * Inits the incoming `primary` and `secondary` values by finding their components and the value of
 * the `component` field.
 *
 * @param {string} primary                  the primary field the logic is going to mutate. E.g., if we're
 *                                          controlling the `state`, it's the primary field.
 * @param {string} secondary                the secondary field the logic is going to mutate. E.g., if we're
 *                                          controlling the `state`, the `country` field is the secondary one,
 *                                          because its appearance has to be altered depending on the state of
 *                                          `state`.
 * @param {ComponentInfo} componentInfo     information about what component to use
 * @param {AttributeInfo} [attributeInfo]   information about what model to use
 *
 * @returns {ReturnValue}
 */
export default (primary, secondary, componentInfo, attributeInfo) => (module, component, event) => {
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]

  const parentComponent = componentInfo.parent
    ? find(actionComponent)[componentInfo.parent]
    : actionComponent
  const { address } = find(parentComponent)
  const { [primary]: primaryComponent, [secondary]: secondaryComponent } = find(address)

  if (!primaryComponent || !secondaryComponent) {
    throw new ReferenceError(`Primary or secondary not defined. Got: ${JSON.stringify(primaryComponent)}, ${JSON.stringify(secondaryComponent)}`)
  }

  const value = getFirstItem(get(component))

  return [
    { primary: primaryComponent, secondary: secondaryComponent, value: value?.key || value, c: componentInfo, a: attributeInfo },
    module,
    component,
    event,
  ]
}
